/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package data.storage

import kotlinx.browser.localStorage
import kotlinx.coroutines.await
import kotlin.js.Promise

/**
 * JavaScript implementation of SecureStorage using Web Crypto API
 * Provides AES-256-GCM encryption for stored data in browser localStorage
 */

// External declarations for Web Crypto API
external interface Crypto {
    val subtle: SubtleCrypto
    fun getRandomValues(array: dynamic): dynamic
}

external interface SubtleCrypto {
    fun generateKey(algorithm: dynamic, extractable: Boolean, keyUsages: Array<String>): Promise<dynamic>
    fun encrypt(algorithm: dynamic, key: dynamic, data: dynamic): Promise<dynamic>
    fun decrypt(algorithm: dynamic, key: dynamic, data: dynamic): Promise<dynamic>
    fun exportKey(format: String, key: dynamic): Promise<dynamic>
    fun importKey(
        format: String,
        keyData: dynamic,
        algorithm: dynamic,
        extractable: Boolean,
        keyUsages: Array<String>,
    ): Promise<dynamic>
}

@Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")
private val crypto: Crypto
    get() = js("window.crypto") as Crypto

actual class SecureStorage {
    private val keyStorageKey = "wiz3r_aes_key"
    private val prefix = "wiz3r_secure_"
    private val encryptedPrefix = "AES:"

    actual suspend fun store(
        key: String,
        value: String,
    ) {
        try {
            val encryptedValue = encrypt(value)
            localStorage.setItem(prefix + key, encryptedValue)
        } catch (e: Exception) {
            // Fallback to base64 if Web Crypto fails (e.g., non-HTTPS)
            console.warn("AES encryption failed, using base64 fallback: ${e.message}")
            val base64 = js("btoa(unescape(encodeURIComponent(value)))") as String
            localStorage.setItem(prefix + key, "B64:$base64")
        }
    }

    actual suspend fun retrieve(key: String): String? {
        val storedValue = localStorage.getItem(prefix + key) ?: return null

        return try {
            when {
                storedValue.startsWith(encryptedPrefix) -> decrypt(storedValue)
                storedValue.startsWith("B64:") -> {
                    // Base64 fallback data
                    val base64 = storedValue.substring(4)
                    js("decodeURIComponent(escape(atob(base64)))") as String
                }
                else -> {
                    // Legacy unencrypted data - return as-is
                    storedValue
                }
            }
        } catch (e: Exception) {
            console.warn("Decryption failed, returning raw value: ${e.message}")
            storedValue
        }
    }

    actual suspend fun delete(key: String) {
        localStorage.removeItem(prefix + key)
    }

    actual suspend fun exists(key: String): Boolean = localStorage.getItem(prefix + key) != null

    actual suspend fun clear() {
        // Preserve authentication tokens during clear
        val authKeys =
            setOf(
                "${prefix}plaid_access_token",
                "${prefix}plaid_item_id",
                "${prefix}user_id",
                "${prefix}auth_token",
                keyStorageKey,
            )

        val keysToRemove = mutableListOf<String>()
        for (i in 0 until localStorage.length) {
            val key = localStorage.key(i)
            if (key?.startsWith(prefix) == true && key !in authKeys) {
                keysToRemove.add(key)
            }
        }
        keysToRemove.forEach { localStorage.removeItem(it) }
    }

    actual suspend fun getAllKeys(): Set<String> {
        val keys = mutableSetOf<String>()
        for (i in 0 until localStorage.length) {
            val key = localStorage.key(i)
            if (key?.startsWith(prefix) == true) {
                keys.add(key.substring(prefix.length))
            }
        }
        return keys
    }

    private suspend fun encrypt(plainText: String): String {
        if (!isWebCryptoAvailable()) {
            throw Exception("Web Crypto API not available")
        }

        val key = getOrCreateKey()

        // Generate random IV (12 bytes for GCM)
        val iv: dynamic = js("new Uint8Array(12)")
        crypto.getRandomValues(iv)

        // Convert text to bytes
        val encoder: dynamic = js("new TextEncoder()")
        val data = encoder.encode(plainText)

        // Encrypt using AES-GCM
        val algorithm =
            js(
                """({
            name: 'AES-GCM',
            iv: iv
        })""",
            )

        val encryptedBuffer: dynamic = crypto.subtle.encrypt(algorithm, key, data).await()
        val encryptedArray: dynamic = js("new Uint8Array(encryptedBuffer)")

        // Combine IV + ciphertext and encode as base64
        val combined: dynamic = js("new Uint8Array(iv.length + encryptedArray.length)")
        combined.set(iv, 0)
        combined.set(encryptedArray, iv.length as Int)

        val base64 = arrayBufferToBase64(combined.buffer)
        return "$encryptedPrefix$base64"
    }

    private suspend fun decrypt(encryptedText: String): String {
        if (!isWebCryptoAvailable()) {
            throw Exception("Web Crypto API not available")
        }

        if (!encryptedText.startsWith(encryptedPrefix)) {
            throw Exception("Invalid encrypted format")
        }

        val key = getOrCreateKey()
        val base64Data = encryptedText.substring(encryptedPrefix.length)
        val combined = base64ToArrayBuffer(base64Data)
        val combinedArray: dynamic = js("new Uint8Array(combined)")

        // Extract IV (first 12 bytes) and ciphertext
        val iv = combinedArray.slice(0, 12)
        val ciphertext = combinedArray.slice(12)

        val algorithm =
            js(
                """({
            name: 'AES-GCM',
            iv: iv
        })""",
            )

        val decryptedBuffer: dynamic = crypto.subtle.decrypt(algorithm, key, ciphertext.buffer).await()

        val decoder: dynamic = js("new TextDecoder()")
        return decoder.decode(decryptedBuffer) as String
    }

    private suspend fun getOrCreateKey(): dynamic {
        // Try to load existing key from localStorage
        val storedKey = localStorage.getItem(keyStorageKey)
        if (storedKey != null) {
            return importKey(storedKey)
        }

        // Generate new AES-256 key
        val algorithm =
            js(
                """({
            name: 'AES-GCM',
            length: 256
        })""",
            )

        val key = crypto.subtle.generateKey(algorithm, true, arrayOf("encrypt", "decrypt")).await<dynamic>()

        // Export and store the key
        val exportedKey = crypto.subtle.exportKey("raw", key).await<dynamic>()
        val keyBase64 = arrayBufferToBase64(exportedKey)
        localStorage.setItem(keyStorageKey, keyBase64)

        return key
    }

    private suspend fun importKey(base64Key: String): dynamic {
        val keyBuffer = base64ToArrayBuffer(base64Key)

        val algorithm =
            js(
                """({
            name: 'AES-GCM'
        })""",
            )

        return crypto.subtle.importKey("raw", keyBuffer, algorithm, true, arrayOf("encrypt", "decrypt")).await<dynamic>()
    }

    private fun arrayBufferToBase64(buffer: dynamic): String {
        // Store buffer in global scope so JS can access it
        val global = js("globalThis")
        global._kotlinBuffer = buffer

        // Create Uint8Array using new keyword
        val bytes: dynamic = js("new Uint8Array(globalThis._kotlinBuffer)")
        js("delete globalThis._kotlinBuffer")

        // Build binary string
        val chunks = mutableListOf<String>()
        val length = (bytes.length as Number).toInt()
        for (i in 0 until length) {
            val byte = (bytes[i] as Number).toInt()
            chunks.add(byte.toChar().toString())
        }
        val binary = chunks.joinToString("")

        // Encode to base64
        return global.btoa(binary) as String
    }

    private fun base64ToArrayBuffer(base64: String): dynamic {
        val global = js("globalThis")

        // Decode from base64
        val binary = global.atob(base64) as String
        val length = binary.length

        // Store length in global scope so JS can access it
        global._kotlinLength = length

        // Create Uint8Array using new keyword
        val bytes: dynamic = js("new Uint8Array(globalThis._kotlinLength)")
        js("delete globalThis._kotlinLength")

        // Fill with character codes
        for (i in 0 until length) {
            bytes[i] = binary[i].code
        }

        return bytes.buffer
    }

    private fun isWebCryptoAvailable(): Boolean =
        try {
            js("!!(typeof window !== 'undefined' && window.crypto && window.crypto.subtle)") as Boolean
        } catch (e: Exception) {
            false
        }
}

/**
 * Factory for creating SecureStorage instances
 */
object SecureStorageFactory {
    fun create(): SecureStorage = SecureStorage()
}
